<template>
  <div
    id="main-body"
    class="leading-normal tracking-normal h-screen bg-gray-50 dark:bg-gray-900 flex overflow-hidden"
  >
    <div class="flex w-full">
      <!-- Sidebar -->
      <div
        id="main-nav"
        class="sidebar-wrapper bg-white dark:bg-gray-900 border-r dark:border-gray-700 z-50 h-screen transform transition-all duration-300 ease-in-out shadow-lg lg:shadow-none fixed inset-y-0 left-0 lg:static"
        :class="[
          sideBarOpen ? 'translate-x-0' : '-translate-x-full',
          'lg:translate-x-0',
          sidebarCollapsed ? 'w-16 lg:w-20' : 'w-64 md:w-72',
        ]"
      >
        <div
          class="w-full h-20 border-b dark:border-gray-700 flex px-4 items-center mb-8"
        >
          <img
            draggable="false"
            loading="lazy"
            src="/logo.png"
            class="w-full max-h-16 object-contain rounded transition-all duration-300 hover:-translate-y-1 transform-gpu"
            alt="Events Logo"
          />
        </div>
        <div class="mb-4 px-4 gap-2 flex flex-col">
          <div class="w-full flex items-center justify-end mb-2">
            <button
              class="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition hidden lg:inline-flex"
              @click="sidebarCollapsed = !sidebarCollapsed"
              :title="sidebarCollapsed ? 'Expand' : 'Collapse'"
            >
              <svg
                v-if="!sidebarCollapsed"
                class="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  d="M3 4a1 1 0 011-1h6a1 1 0 010 2H6.414l9.293 9.293a1 1 0 01-1.414 1.414L5 6.414V10a1 1 0 11-2 0V4z"
                />
              </svg>
              <svg
                v-else
                class="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  d="M17 16a1 1 0 01-1 1H10a1 1 0 110-2h3.586L4.293 5.707A1 1 0 015.707 4.293L16 14.586V11a1 1 0 112 0v5z"
                />
              </svg>
            </button>
          </div>
          <div
            :class="
              $route.path == '/admin' ? 'bg-gray-200 dark:bg-gray-800' : ''
            "
            class="w-full flex items-center text-gray-700 dark:text-gray-200 h-10 pl-4 transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg cursor-pointer group"
            @click="$router.push('/admin') && toggleSidebar()"
          >
            <svg
              viewBox="0 0 20 20"
              class="h-6 w-6 fill-current mr-2 text-gray-600 dark:text-gray-300"
              fill="currentColor"
            >
              <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"></path>
            </svg>
            <span
              class="text-gray-800 dark:text-gray-100 group-hover:dark:text-white"
              :class="{
                'dark:text-white font-semibold': $route.path == '/admin',
              }"
              v-show="!sidebarCollapsed"
              >Dashboard</span
            >
          </div>
          <div
            :class="
              $route.path.startsWith('/admin/listings')
                ? 'bg-gray-200 dark:bg-gray-800'
                : ''
            "
            class="w-full flex items-center text-gray-700 dark:text-gray-200 h-10 pl-4 transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg cursor-pointer group"
            @click="$router.push('/admin/listings') && toggleSidebar()"
          >
            <svg
              viewBox="0 0 20 20"
              class="h-6 w-6 fill-current mr-2 text-gray-600 dark:text-gray-300"
              fill="currentColor"
            >
              <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"></path>
            </svg>
            <span
              class="text-gray-800 dark:text-gray-100 group-hover:dark:text-white"
              :class="{
                'dark:text-white font-semibold':
                  $route.path.startsWith('/admin/listings'),
              }"
              v-show="!sidebarCollapsed"
              >Listings</span
            >
          </div>
          <div
            :class="
              $route.path == '/admin/campaigns'
                ? 'bg-gray-200 dark:bg-gray-800'
                : ''
            "
            class="w-full flex items-center text-gray-700 dark:text-gray-200 h-10 pl-4 transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg cursor-pointer group"
            @click="$router.push('/admin/campaigns') && toggleSidebar()"
          >
            <svg
              viewBox="0 0 20 20"
              class="h-6 w-6 fill-current mr-2 text-gray-600 dark:text-gray-300"
              fill="currentColor"
            >
              <path fill-rule="evenodd" d="M18 3a1 1 0 00-1.447-.894L8.763 6H5a3 3 0 000 6h.28l1.771 5.316A1 1 0 008 18h1a1 1 0 001-1v-4.382l6.553 3.276A1 1 0 0018 15V3z" clip-rule="evenodd"></path>
            </svg>
            <span
              class="text-gray-800 dark:text-gray-100 group-hover:dark:text-white"
              :class="{
                'dark:text-white font-semibold':
                  $route.path == '/admin/campaigns',
              }"
              v-show="!sidebarCollapsed"
              >Campaigns</span
            >
          </div>
          <div
            :class="
              $route.path == '/admin/property-types'
                ? 'bg-gray-200 dark:bg-gray-800'
                : ''
            "
            class="w-full flex items-center text-gray-700 dark:text-gray-200 h-10 pl-4 transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg cursor-pointer group"
            @click="$router.push('/admin/property-types') && toggleSidebar()"
          >
            <svg
              viewBox="0 0 20 20"
              class="h-6 w-6 fill-current mr-2 text-gray-600 dark:text-gray-300"
              fill="currentColor"
            >
              <path fill-rule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path>
            </svg>
            <span
              class="text-gray-800 dark:text-gray-100 group-hover:dark:text-white"
              :class="{
                'dark:text-white font-semibold':
                  $route.path == '/admin/property-types',
              }"
              v-show="!sidebarCollapsed"
              >Property Types</span
            >
          </div>
          <div
            :class="
              $route.path == '/admin/locations'
                ? 'bg-gray-200 dark:bg-gray-800'
                : ''
            "
            class="w-full flex items-center text-gray-700 dark:text-gray-200 h-10 pl-4 transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg cursor-pointer group"
            @click="$router.push('/admin/locations') && toggleSidebar()"
          >
            <svg
              viewBox="0 0 20 20"
              class="h-6 w-6 fill-current mr-2 text-gray-600 dark:text-gray-300"
              fill="currentColor"
            >
              <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"></path>
            </svg>
            <span
              class="text-gray-800 dark:text-gray-100 group-hover:dark:text-white"
              :class="{
                'dark:text-white font-semibold':
                  $route.path == '/admin/locations',
              }"
              v-show="!sidebarCollapsed"
              >Locations</span
            >
          </div>
          <div
            :class="
              $route.path == '/admin/testimonials'
                ? 'bg-gray-200 dark:bg-gray-800'
                : ''
            "
            class="w-full flex items-center text-gray-700 dark:text-gray-200 h-10 pl-4 transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg cursor-pointer group"
            @click="$router.push('/admin/testimonials') && toggleSidebar()"
          >
            <svg
              viewBox="0 0 20 20"
              class="h-6 w-6 fill-current mr-2 text-gray-600 dark:text-gray-300"
              fill="currentColor"
            >
              <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
            </svg>
            <span
              class="text-gray-800 dark:text-gray-100 group-hover:dark:text-white"
              :class="{
                'dark:text-white font-semibold':
                  $route.path == '/admin/testimonials',
              }"
              v-show="!sidebarCollapsed"
              >Testimonials</span
            >
          </div>
          <div
            :class="
              $route.path == '/admin/profile'
                ? 'bg-gray-200 dark:bg-gray-800'
                : ''
            "
            class="w-full flex items-center text-gray-700 dark:text-gray-200 h-10 pl-4 transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg cursor-pointer group"
            @click="$router.push('/admin/profile') && toggleSidebar()"
          >
            <svg
              viewBox="0 0 20 20"
              class="h-6 w-6 fill-current mr-2 text-gray-600 dark:text-gray-300"
              fill="currentColor"
            >
              <path d="M10 10a4 4 0 100-8 4 4 0 000 8z"></path>
              <path
                fill-rule="evenodd"
                d="M.458 16.042A10 10 0 1119.542 16.042 8 8 0 0010 12a8 8 0 00-9.542 4.042z"
                clip-rule="evenodd"
              ></path>
            </svg>
            <span
              class="text-gray-800 dark:text-gray-100 group-hover:dark:text-white"
              :class="{
                'dark:text-white font-semibold':
                  $route.path == '/admin/profile',
              }"
              v-show="!sidebarCollapsed"
              >Profile</span
            >
          </div>
          <div
            :class="
              $route.path == '/admin/submissions'
                ? 'bg-gray-200 dark:bg-gray-800'
                : ''
            "
            class="w-full flex items-center text-gray-700 dark:text-gray-200 h-10 pl-4 transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg cursor-pointer group"
            @click="$router.push('/admin/submissions') && toggleSidebar()"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6 fill-current mr-2 text-gray-600 dark:text-gray-300"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
              <path
                fill-rule="evenodd"
                d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                clip-rule="evenodd"
              />
            </svg>
            <span
              class="text-gray-800 dark:text-gray-100 group-hover:dark:text-white"
              :class="{
                'dark:text-white font-semibold':
                  $route.path == '/admin/submissions',
              }"
              v-show="!sidebarCollapsed"
              >Submissions</span
            >
          </div>
          <hr class="hr my-4 border-gray-200 dark:border-gray-700" />
          <div
            :class="
              $route.path == '/settings' ? 'bg-gray-200 dark:bg-gray-800' : ''
            "
            @click="$router.push('/settings')"
            class="w-full flex items-center text-gray-700 dark:text-gray-200 h-10 pl-4 transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg cursor-pointer group"
          >
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              class="h-6 w-6 fill-current mr-2 text-gray-600 dark:text-gray-300"
            >
              <path
                d="M17.498,11.697c-0.453-0.453-0.704-1.055-0.704-1.697c0-0.642,0.251-1.244,0.704-1.697c0.069-0.071,0.15-0.141,0.257-0.22c0.127-0.097,0.181-0.262,0.137-0.417c-0.164-0.558-0.388-1.093-0.662-1.597c-0.075-0.141-0.231-0.22-0.391-0.199c-0.13,0.02-0.238,0.027-0.336,0.027c-1.325,0-2.401-1.076-2.401-2.4c0-0.099,0.008-0.207,0.027-0.336c0.021-0.158-0.059-0.316-0.199-0.391c-0.503-0.274-1.039-0.498-1.597-0.662c-0.154-0.044-0.32,0.01-0.416,0.137c-0.079,0.106-0.148,0.188-0.22,0.257C11.244,2.956,10.643,3.207,10,3.207c-0.642,0-1.244-0.25-1.697-0.704c-0.071-0.069-0.141-0.15-0.22-0.257C7.987,2.119,7.821,2.065,7.667,2.109C7.109,2.275,6.571,2.497,6.07,2.771C5.929,2.846,5.85,3.004,5.871,3.162c0.02,0.129,0.027,0.237,0.027,0.336c0,1.325-1.076,2.4-2.401,2.4c-0.098,0-0.206-0.007-0.335-0.027C3.001,5.851,2.845,5.929,2.77,6.07C2.496,6.572,2.274,7.109,2.108,7.667c-0.044,0.154,0.01,0.32,0.137,0.417c0.106,0.079,0.187,0.148,0.256,0.22c0.938,0.936,0.938,2.458,0,3.394c-0.069,0.072-0.15,0.141-0.256,0.221c-0.127,0.096-0.181,0.262-0.137,0.416c0.166,0.557,0.388,1.096,0.662,1.596c0.075,0.143,0.231,0.221,0.392,0.199c0.129-0.02,0.237-0.027,0.335-0.027c1.325,0,2.401,1.076,2.401,2.402c0,0.098-0.007,0.205-0.027,0.334C5.85,16.996,5.929,17.154,6.07,17.23c0.501,0.273,1.04,0.496,1.597,0.66c0.154,0.047,0.32-0.008,0.417-0.137c0.079-0.105,0.148-0.186,0.22-0.256c0.454-0.453,1.055-0.703,1.697-0.703c0.643,0,1.244,0.25,1.697,0.703c0.071,0.07,0.141,0.15,0.22,0.256c0.073,0.098,0.188,0.152,0.307,0.152c0.036,0,0.073-0.004,0.109-0.016c0.558-0.164,1.096-0.387,1.597-0.66c0.141-0.076,0.22-0.234,0.199-0.393c-0.02-0.129-0.027-0.236-0.027-0.334c0-1.326,1.076-2.402,2.401-2.402c0.098,0,0.206,0.008,0.336,0.027c0.159,0.021,0.315-0.057,0.391-0.199c0.274-0.5,0.496-1.039,0.662-1.596c0.044-0.154-0.01-0.32-0.137-0.416C17.648,11.838,17.567,11.77,17.498,11.697 M16.671,13.334c-0.059-0.002-0.114-0.002-0.168-0.002c-1.749,0-3.173,1.422-3.173,3.172c0,0.053,0.002,0.109,0.004,0.166c-0.312,0.158-0.64,0.295-0.976,0.406c-0.039-0.045-0.077-0.086-0.115-0.123c-0.601-0.6-1.396-0.93-2.243-0.93s-1.643,0.33-2.243,0.93c-0.039,0.037-0.077,0.078-0.116,0.123c-0.336-0.111-0.664-0.248-0.976-0.406c0.002-0.057,0.004-0.113,0.004-0.166c0-1.75-1.423-3.172-3.172-3.172c-0.054,0-0.11,0-0.168,0.002c-0.158-0.312-0.293-0.639-0.405-0.975c0.044-0.039,0.085-0.078,0.124-0.115c1.236-1.236,1.236-3.25,0-4.486C3.009,7.719,2.969,7.68,2.924,7.642c0.112-0.336,0.247-0.664,0.405-0.976C3.387,6.668,3.443,6.67,3.497,6.67c1.75,0,3.172-1.423,3.172-3.172c0-0.054-0.002-0.11-0.004-0.168c0.312-0.158,0.64-0.293,0.976-0.405C7.68,2.969,7.719,3.01,7.757,3.048c0.6,0.6,1.396,0.93,2.243,0.93s1.643-0.33,2.243-0.93c0.038-0.039,0.076-0.079,0.115-0.123c0.336,0.112,0.663,0.247,0.976,0.405c-0.002,0.058-0.004,0.114-0.004,0.168c0,1.749,1.424,3.172,3.173,3.172c0.054,0,0.109-0.002,0.168-0.004c0.158,0.312,0.293,0.64,0.405,0.976c-0.045,0.038-0.086,0.077-0.124,0.116c-0.6,0.6-0.93,1.396-0.93,2.242c0,0.847,0.33,1.645,0.93,2.244c0.038,0.037,0.079,0.076,0.124,0.115C16.964,12.695,16.829,13.021,16.671,13.334 M10,5.417c-2.528,0-4.584,2.056-4.584,4.583c0,2.529,2.056,4.584,4.584,4.584s4.584-2.055,4.584-4.584C14.584,7.472,12.528,5.417,10,5.417 M10,13.812c-2.102,0-3.812-1.709-3.812-3.812c0-2.102,1.71-3.812,3.812-3.812c2.102,0,3.812,1.71,3.812,3.812C13.812,12.104,12.102,13.812,10,13.812"
              ></path>
            </svg>
            <span
              class="text-gray-800 dark:text-gray-100 group-hover:dark:text-white"
              :class="{
                'dark:text-white font-semibold': $route.path == '/settings',
              }"
              v-show="!sidebarCollapsed"
              >Settings</span
            >
          </div>
        </div>
      </div>
      <!-- Overlay for mobile sidebar -->
      <div
        v-if="sideBarOpen"
        @click="toggleSidebar"
        class="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden transition-opacity duration-300 ease-in-out"
      ></div>

      <!-- Main Content -->
      <div
        id="main-content"
        class="flex-1 flex flex-col bg-gray-50 dark:bg-gray-900 transition-all duration-300 dark:text-white pl-0 overflow-hidden"
      >
        <!-- Top Bar -->
        <div class="sticky top-0 z-30 bg-white dark:bg-gray-900 border-b dark:border-gray-700">
          <div
            class="w-full h-14 px-4 bg-white dark:bg-gray-900 flex items-center justify-between transition-all duration-300"
          >
            <!-- left navbar - mobile hamburger + breadcrumb -->
            <div class="flex items-center gap-3">
              <!-- mobile hamburger -->
              <div class="inline-block lg:hidden flex items-center">
                <button
                  class="navbar-burger p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600"
                  @click="toggleSidebar()"
                  aria-label="Toggle sidebar"
                >
                  <svg
                    class="h-6 w-6"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                  >
                    <title>Menu</title>
                    <path
                      fill-rule="evenodd"
                      d="M3 5h14v2H3V5zm0 4h10v2H3V9zm0 4h14v2H3v-2z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              </div>
          <!-- Breadcrumb -->
              <nav class="hidden md:block" aria-label="Breadcrumb">
            <ol
              class="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400"
            >
              <li>
                <router-link
                  to="/admin"
                  class="flex items-center hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4 mr-1.5 flex-shrink-0"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"
                    />
                  </svg>
                  <span class="font-medium">Home</span>
                </router-link>
              </li>
              <li
                v-if="
                  $route.path.startsWith('/admin/listings/new') ||
                  $route.path.startsWith('/admin/listings/edit/')
                "
                class="flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-3.5 w-3.5 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                <router-link
                  to="/admin/listings"
                  class="ml-2 font-medium hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
                >
                  Listings
                </router-link>
              </li>
              <li class="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-3.5 w-3.5 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                <span
                  class="ml-2 font-semibold text-gray-800 dark:text-white truncate max-w-[200px]"
                  :title="$route.meta.name || 'Current Page'"
                  >{{ $route.meta.name || "Page" }}</span
                >
              </li>
            </ol>
          </nav>
            </div>

            <!-- right navbar - notifications + profile -->
            <div class="flex items-center relative" v-if="userInfo">
              <svg
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
                height="24"
                viewBox="0 0 24 24"
                width="24"
                class="fill-current mr-3 text-gray-600 dark:text-gray-400 hover:text-tahiti-500 dark:hover:text-white cursor-pointer"
              >
                <path d="M0 0h24v24H0z" fill="none" />
                <path
                  d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z"
                />
              </svg>
              <img
                loading="lazy"
                :src="
                  userInfo.imageUrl
                    ? userInfo.imageUrl
                    : 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTNzXYh-X4wxX1jfbPywa8HWoNGDnx1Tlo0-g&s'
                "
                class="w-10 h-10 rounded-full shadow-lg cursor-pointer border-2 border-transparent hover:border-tahiti-500"
                @click="dropDownOpen = !dropDownOpen"
                alt="User avatar"
              />
            </div>
            <div v-else>
              <!-- Placeholder or login button if user not loaded -->
            </div>
          </div>

          <!-- dropdown menu -->
          <div
            v-show="dropDownOpen"
            class="absolute right-0 mt-2 mr-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl text-gray-700 dark:text-gray-300 rounded-lg w-48 z-50 transform transition-all duration-150 ease-out"
            :class="
              dropDownOpen
                ? 'opacity-100 scale-100'
                : 'opacity-0 scale-95 pointer-events-none'
            "
          >
            <a
              href="#"
              class="block px-4 py-2 text-sm hover:bg-tahiti-100 dark:hover:bg-gray-700"
              >Account
            </a>
            <router-link
              to="/settings"
              class="block px-4 py-2 text-sm hover:bg-tahiti-100 dark:hover:bg-gray-700"
              @click="dropDownOpen = false"
              >Settings</router-link
            >
            <a
              @click="logOut"
              href="#"
              class="block px-4 py-2 text-sm hover:bg-tahiti-100 dark:hover:bg-gray-700 border-t border-gray-200 dark:border-gray-700"
              >Logout</a
            >
          </div>
          <!-- dropdown menu end -->
        </div>

        <!-- Page Content -->
        <div
          class="flex-1 pr-6 pb-6 pl-0 lg:pl-6 bg-gray-50 dark:bg-gray-900 mb-0 transition-all duration-300 overflow-y-auto"
        >
          <!-- Router View -->
          <router-view v-slot="{ Component, route }">
            <transition name="fade" mode="out-in">
              <component :is="Component" :key="route.path" />
            </transition>
          </router-view>
        </div>

        <!-- Footer -->
        <div
          class="w-full border-t dark:border-gray-700 px-6 py-4 flex flex-col sm:flex-row sm:justify-between items-center sticky bottom-0 bg-white dark:bg-gray-900"
        >
          <p class="mb-2 sm:mb-0 text-sm text-gray-600 dark:text-gray-400">
            © Copyright {{ new Date().getFullYear() }} Amadeen Consult
          </p>
          <div class="flex text-sm">
            <router-link
              to="/terms-and-conditions"
              class="mr-4 text-gray-600 dark:text-gray-400 hover:text-tahiti-600 dark:hover:text-white"
              >Terms & Conditions</router-link
            ><router-link
              to="/privacy-policy"
              class="mr-4 text-gray-600 dark:text-gray-400 hover:text-tahiti-600 dark:hover:text-white"
              >Privacy Policy</router-link
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import { mapState } from "vuex";
import axios from "axios";
export default {
  components: {},
  computed: {
    ...mapState(["sideBarOpen"]),
  },
  name: "DashboardSection",
  data() {
    return {
      dropDownOpen: false,
      userId: computed(() => {
        return window.localStorage.getItem("userId");
      }),
      userInfo: null,
      sidebarCollapsed: false,
    };
  },
  created() {
    if (this.$store.getters.isLoggedIn) {
      this.getUserInfo();
    }
    // Close sidebar on initial load on mobile
    if (window.innerWidth < 1024) {
      this.$store.commit("closeSidebar");
    }
  },
  methods: {
    getUserInfo() {
      let url = process.env.VUE_APP_IP_ADDRESS + "/api/v1/profile";

      const config = {
        headers: {
          "Content-Type": "application/json",
          "x-token": this.userId,
          "x-source-user": "CUSTOMER",
        },
      };

      axios
        .get(url, config)
        .then((response) => {
          var res = response.data;
          this.userInfo = res.body;
          return res;
        })
        .catch((error) => {
          if (error) {
            this.errorMessage = error.response.data.body.debugMessage;
            this.response = error.response.data;

            // Assuming toast is available globally or imported/setup correctly
            if (this.toast) {
              this.toast.error(this.errorMessage);
            }
          }
        });
    },
    toggleSidebar() {
      this.$store.dispatch("toggleSidebar");
    },
    logOut() {
      var user = {};
      user.domain = "business";
      user.origin = "shop";
      this.$store.commit("dropUserInfo");
      this.$store.commit("updateLoginStatus", false);
      this.$store.dispatch("logout", user);
      this.$router.push("/login").then(() => {
        this.$router.go();
      });
    },
  },
};
</script>

<style scoped>
/* Add styles for the fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease-in-out;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.bg-tahiti-50 {
  background-color: #fdf4f2;
}
.bg-tahiti-100 {
  background-color: #fcddd5;
}
.text-tahiti-500 {
  color: #ee4e34;
}
.bg-tahiti-500 {
  background-color: #ee4e34;
}
.bg-tahiti-600 {
  background-color: #d63b23;
}
.focus\:ring-tahiti-500:focus {
  --tw-ring-color: #ee4e34;
}
.hover\:bg-tahiti-600:hover {
  background-color: #d63b23;
}
</style>
